import { useState, useRef, ComponentPropsWithoutRef } from 'react';

import { X } from 'lucide-react';

import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@web/components/ui/command';
import { ScrollArea } from './scroll-area';
import { Button } from './button';
import { cn } from '@web/lib/shadcn';

export function Combobox({
  className,
  items,
  inputPlaceholder,
  stateManager: externalStateManager,
}: ComponentPropsWithoutRef<typeof Command> & {
  items: string[];
  inputPlaceholder?: string;
  stateManager?: [string[], React.Dispatch<React.SetStateAction<string[]>>];
}) {
  const internalStateManager = useState<string[]>([]);

  const stateManager = externalStateManager ?? internalStateManager;
  const [selectedItems, setSelectedItems] = stateManager;

  const nonSelectedItems = items.filter(
    (item) => !selectedItems.includes(item),
  );

  const toggleItem = (item: string) => {
    setSelectedItems((current) =>
      current.includes(item)
        ? current.filter((i) => i !== item)
        : [...current, item],
    );
  };

  const removeItem = (item: string) => {
    setSelectedItems((current) => current.filter((i) => i !== item));
  };

  const [search, setSearch] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Command
      className={cn('h-auto w-[450px] rounded-lg border shadow-md', className)}
    >
      <div className="flex flex-wrap items-center gap-1 border-b p-2 [&>*]:border-none">
        {selectedItems.map((item) => (
          <span
            key={item}
            className="bg-primary text-primary-foreground flex items-center gap-1 rounded-md px-2 py-1 text-xs"
          >
            {item}
            <Button
              variant="destructive"
              size="icon"
              className="h-4 w-4 bg-transparent"
              onClick={(e) => {
                e.stopPropagation();
                removeItem(item);
                inputRef.current?.focus();
              }}
              aria-label={`Remove ${item}`}
            >
              <X className="stroke-muted-foreground h-3 w-3" />
            </Button>
          </span>
        ))}

        <CommandInput
          ref={inputRef}
          value={search}
          onValueChange={setSearch}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && search === '') {
              removeItem(selectedItems[selectedItems.length - 1]);
            }
          }}
          placeholder={inputPlaceholder ?? 'Add'}
          className="h-auto px-0.5 py-0"
        />
      </div>

      <ScrollArea viewportClassName="max-h-40">
        <CommandList className="max-h-max">
          <CommandEmpty>No match</CommandEmpty>

          {nonSelectedItems.map((item) => (
            <CommandItem
              key={item}
              onSelect={() => {
                toggleItem(item);
                setSearch('');
                inputRef.current?.focus();
              }}
            >
              <span>{item}</span>
            </CommandItem>
          ))}
        </CommandList>
      </ScrollArea>
    </Command>
  );
}
